import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { fitMiddleEllipsis } from './middle-ellipsis';

/**
 * Middle-ellipsis against a full-width measure node (`widthRef`).
 * Keep the visible text + trailing icon in a shrink-wrapped group so the icon
 * stays next to the address, not pinned to the row edge.
 */
export function useFitMiddleEllipsis(
  value: string,
  reservePx = 0,
): {
  text: string;
  widthRef: RefObject<HTMLDivElement | null>;
} {
  const widthRef = useRef<HTMLDivElement | null>(null);
  const [text, setText] = useState(value);

  useLayoutEffect(() => {
    const el = widthRef.current;
    if (!el || !value) {
      setText(value);
      return;
    }

    const style = getComputedStyle(el);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setText(value);
      return;
    }

    ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const measure = (s: string) => ctx.measureText(s).width;

    const update = () => {
      const width = Math.max(0, el.clientWidth - reservePx);
      if (width <= 0) {
        setText(value);
        return;
      }
      setText(fitMiddleEllipsis(value, width, measure));
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value, reservePx]);

  return { text, widthRef };
}
